import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Button,
  Center,
  Grid,
  ThemeIcon,
  Box,
  Badge
} from '@mantine/core';
import {
  IconSettings,
  IconUsers,
  IconPhone,
  IconLock,
  IconPlayerPlay,
  IconTools
} from '@tabler/icons-react';

const Tools = ({ soapCredentials, onSubmit = () => {} }) => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    console.log(`Submitting credentials for: ${path}`, soapCredentials);

    // Submit credentials with the selected path
    onSubmit(soapCredentials, path);

    // Navigate to the target page
    navigate(path);
  };

  // Icon mapping for different tools
  const toolIcons = {
    contextservices: IconSettings,
    fedimport: IconUsers,
    speeddialer: IconPhone,
    passwordupdate: IconLock,
    autoanswer: IconPlayerPlay
  };

  // Define the tools
  const tools = {
    contextservices: {
      name: "Context Services Build Out",
      description: "Build out context services configuration",
      route: "/contextservices"
    },
    fedimport: {
      name: "Federation ID Import",
      description: "Import federation IDs for user management",
      route: "/fedimport"
    },
    speeddialer: {
      name: "Speed Dial Import",
      description: "Import speed dial configurations for users",
      route: "/speedimport"
    },
    passwordupdate: {
      name: "Password Update",
      description: "Update user passwords in bulk",
      route: "/passwordupdate"
    },
    autoanswer: {
      name: "Auto Answer Import",
      description: "Configure auto answer settings for users",
      route: "/autoanswer"
    }
  };

  return (
    <Box
      style={{
        minHeight: '80vh',
        background: 'linear-gradient(135deg, #2e7d32 0%, #e8f5e8 100%)',
        padding: '40px 20px',
        borderRadius: '16px'
      }}
    >
      <Container size="lg">
        <Center>
          <Stack align="center" gap="xl" style={{ maxWidth: '1200px' }}>
            {/* Header Section */}
            <Stack align="center" gap="md">
              <Title
                order={1}
                size="2.5rem"
                fw={700}
                style={{ color: 'white', textAlign: 'center' }}
              >
                Five9 Tools
              </Title>
              <Text
                size="lg"
                style={{ color: 'rgba(255, 255, 255, 0.9)', textAlign: 'center' }}
              >
                Processing with credentials: <Badge color="green" variant="light" size="lg">{soapCredentials?.username || "Not provided"}</Badge>
              </Text>
            </Stack>

            {/* Tools Selection Grid */}
            <Grid gutter="xl" style={{ width: '100%' }}>
              {Object.keys(tools).map((key) => {
                const tool = tools[key];
                const IconComponent = toolIcons[key] || IconTools;

                return (
                  <Grid.Col key={key} span={{ base: 12, sm: 6, md: 4 }}>
                    <Card
                      shadow="xl"
                      padding="xl"
                      radius="md"
                      style={{
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: '280px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                      }}
                      onClick={() => handleNavigation(tool.route)}
                    >
                      <Stack align="center" gap="md" style={{ flex: 1 }}>
                        <ThemeIcon
                          size={60}
                          radius="md"
                          color="green"
                          variant="light"
                        >
                          <IconComponent size={36} />
                        </ThemeIcon>

                        <Title order={4} size="1.1rem" fw={600} align="center">
                          {tool.name}
                        </Title>

                        <Text
                          size="sm"
                          color="dimmed"
                          align="center"
                          style={{ lineHeight: 1.4, flex: 1 }}
                        >
                          {tool.description}
                        </Text>

                        <Button
                          color="green"
                          size="sm"
                          radius="md"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigation(tool.route);
                          }}
                          style={{ fontWeight: 600 }}
                        >
                          Select Tool
                        </Button>
                      </Stack>
                    </Card>
                  </Grid.Col>
                );
              })}
            </Grid>

            {/* Footer Info */}
            <Stack align="center" gap="xs" style={{ marginTop: '20px' }}>
              <Text
                size="sm"
                style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}
              >
                Five9 SOAP-based configuration and automation tools
              </Text>
            </Stack>
          </Stack>
        </Center>
      </Container>
    </Box>
  );
};

export default Tools;
